// Protocol documentation shown under #/docs. Every number here is the deployed default (script/Deploy.s.sol,
// the contract initializers, offchain/src/config.ts); update this file when one of them changes.
import type { ReactNode } from 'react';

import { TourLink } from '../components/Tour.tsx';
import { config } from '../config.ts';
import { BurnProjection, SoftLaunchBreakEven } from './BurnProjection.tsx';
import { breakEvenPrice, budgetPerDollar, fmtPrice, tailUsdAtRate, yearlyBurnShare } from './projection.ts';

// this deployment's max leverage (VITE_MAX_LEVERAGE): the canary runs a lower one than mainnet
const MAX_LEV = `${config.maxLeverage}×`;
const MAX_MARGIN = `$${config.maxMarginUsd.toLocaleString('en-US')}`;
const MAX_NOTIONAL = `$${config.maxNotionalUsd.toLocaleString('en-US')}`;
const MIN_MARGIN = `$${config.minMarginUsd.toLocaleString('en-US')}`;
const MIN_STAKE = config.minStake.toLocaleString('en-US');
// the PAPURR revenue's share of every loss (VITE_STAKER_LOSS_PCT), beside the protocol's 1%; the buyback splits
// that revenue between the stakers' stream (STREAM_SHARE) and the burn auction's pot (AUCTION_SHARE)
const STK = `${config.stakerLossPct}%`;
// the fee on a winning close (VITE_WIN_FEE_PCT), half protocol, half PAPURR revenue
const WIN_FEE = `${config.winFeePct}%`;
// what HYPE's curve pays of a gain, (1 − h) × x / (x + 1/A + h/B) with h = 0.1, A = 15,000, B = 315; and after the fee
const curvePays = (move: number) => (0.9 * move) / (move + 1 / 15_000 + 0.1 / 315);
const pct = (v: number) => `${Math.round(v * 100)}%`;
const afterFee = (move: number) => pct(curvePays(move) * (1 - config.winFeePct / 100));
// millions of USD as text: $300k, $6.2M, $140M
const usdM = (m: number) => (m < 1 ? `$${Math.round(m * 1000)}k` : `$${m < 10 ? Math.round(m * 10) / 10 : Math.round(m)}M`);
// the keeper's open interest caps (VITE_OI_MIN_CAP_USD, VITE_OI_HARD_CAP_USD)
const OI_MIN = usdM(config.oiMinCapUsd / 1e6);
const OI_MAX = usdM(config.oiHardCapUsd / 1e6);
// the mint curve: the flat part ends at FLAT of house, the tail rate is 100 × (K / (K + T))²
const FLAT = usdM(config.mintFlatM);
const K = config.mintTailKM;
// the boost of the curve's rate under the flat part, at an empty house (VITE_MINT_LOW_HOUSE_X); 0 = the flat rate itself
const LOW_X = config.mintLowHouseX;
// the burn auction's share of every HYPE lot (VITE_AUCTION_PCT)
const AUCTION_SHARE = `${config.auctionPct}%`;
const STREAM_SHARE = `${100 - config.auctionPct}%`;
// the auction's budget per $1 of net loss: its share of the stakers' share, in cents
const BUDGET = budgetPerDollar(config.auctionPct / 100, config.stakerLossPct / 100);
const BUDGET_CENTS = `${Number((BUDGET * 100).toPrecision(3))}¢`;
const breakEven = (rate: number) => fmtPrice(breakEvenPrice(rate, config.auctionPct / 100, config.stakerLossPct / 100));
const burnShare = (yieldPct: number) => `${Number((yearlyBurnShare(yieldPct, 100, config.auctionPct / 100) * 100).toPrecision(3))}%`;
/** Tail losses after which the curve mints `rate` per $1, in millions */
const tailAt = (rate: number) => usdM(tailUsdAtRate(rate, 100, config.mintTailKM * 1e6) / 1e6);
// what the tail can ever mint, 100 × K: "800 million" or "6 billion"
const CURVE_TOTAL = 100 * config.mintTailKM >= 1000 ? `${(100 * config.mintTailKM) / 1000} billion` : `${100 * config.mintTailKM} million`;
const fmtMoney = (usd: number) => `$${usd.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
const KM = usdM(K);
const tailRate = (tM: number) => 100 * (K / (K + tM)) ** 2;
// the tail volume at which the rate halves: K × (√2 − 1)
const HALF_M = K * (Math.SQRT2 - 1);
const rateText = (v: number) => (v >= 10 ? `${Math.round(v)}` : v.toFixed(2).replace(/0$/, ''));
// the house liquidity above which the PAPURR revenue takes a share of its gains (VITE_REWARD_THRESHOLD_M)
const THRESHOLD = usdM(config.rewardThresholdM);
// the PAPURR revenue's share of each new house record (VITE_NEW_HIGH_PCT)
const NEW_HIGH = `${config.newHighPct}%`;
// $100 lost while the house sits at a new record above THRESHOLD: $1 to the protocol, STK plus NEW_HIGH of what the
// house gains to the PAPURR revenue (split between the stream and the pot), the rest kept by the house
const EX_REVENUE = config.stakerLossPct + (config.newHighPct / 100) * (99 - config.stakerLossPct);
const exUsd = (v: number) => `$${v.toFixed(2)}`;
const EX_STREAM = exUsd((EX_REVENUE * (100 - config.auctionPct)) / 100);
const EX_POT = exUsd((EX_REVENUE * config.auctionPct) / 100);
const EX_HOUSE = exUsd(99 - EX_REVENUE);

export type DocPage = { slug: string; title: string; lead: string; body: ReactNode };
export type DocSection = { label: string; pages: DocPage[] };

/** Link to another docs page */
function L({ to, children }: { to: string; children: ReactNode }) {
  return <a href={`#/docs/${to}`}>{children}</a>;
}

function Note({ children }: { children: ReactNode }) {
  return <aside className="doc-note">{children}</aside>;
}

function Formula({ children }: { children: string }) {
  return <pre className="doc-formula">{children}</pre>;
}

const intro: DocPage[] = [
  {
    slug: 'intro/what-is-papurrtrade',
    title: 'What is Papurrtrade?',
    lead: 'Papurrtrade is an onchain perpetuals exchange on Hyperliquid, dedicated to one market: HYPE.',
    body: (
      <>
        <ul>
          <li>Up to {MAX_LEV} leverage on HYPE</li>
          <li>Market orders in one click, limit orders and take profits that wait for your price</li>
          <li>Fills at Hyperliquid's prices: the worse of the book mid and the mark, about 1 bp apart</li>
          <li>No funding payments</li>
          <li>No gas for traders: a relayer submits your signed orders</li>
          <li>PAPURR, minted to traders on their losses, earns a share of the protocol's revenue when staked</li>
        </ul>
        <h2 id="how-it-runs">How it runs</h2>
        <p>
          The contracts live on HyperEVM, Hyperliquid's EVM chain. They read HYPE's order book and mark price
          from HyperCore precompiles, in the block where your trade executes, and fill you at the worse of the
          book mid and the mark. Nothing is traded on Hyperliquid itself: every position is a synthetic contract
          between you and the house.
        </p>
        <h2 id="the-house">The house</h2>
        <p>
          The house is the protocol-owned pool that takes the other side of every trade. It pays winners and
          keeps what losers lose, minus the protocol's fee and the PAPURR revenue, shared by PAPURR stakers and the burn auction. When the house
          cannot pay a win on the spot, the win waits in a first-in, first-out queue and is paid as the house
          refills. See <L to="learn/house-queue">The house and the payout queue</L>.
        </p>
        <h2 id="costs">What it costs</h2>
        <p>
          There is no fee on the size of your position. The protocol earns on outcomes: a payout curve and a{' '}
          {WIN_FEE} fee shape what a winning close pays, and a slice of every loss goes to the protocol and to PAPURR (its stakers and its burn auction)
          without adding anything to what you lose. See <L to="learn/wins-losses">Wins and losses</L>.
        </p>
      </>
    ),
  },
  {
    slug: 'intro/quick-start',
    title: 'Quick start',
    lead: 'From a wallet to a first trade in a couple of minutes.',
    body: (
      <>
        <Note>
          New here? <TourLink>Take the one-minute tour</TourLink>: it walks through the app page by page, and
          you can replay it any time from the ? in the header.
        </Note>
        <h2 id="what-you-need">What you need</h2>
        <ul>
          <li>An EVM wallet: Rabby, MetaMask, or any wallet your browser exposes.</li>
          <li>
            USDC on HyperCore (Hyperliquid spot) or on HyperEVM: at least the minimum margin, {MIN_MARGIN} (the order
            ticket shows the live limits).
          </li>
        </ul>
        <h2 id="connect">1. Connect</h2>
        <p>
          Click <b>Connect Wallet</b>. Before your first trade the app asks for one signature that enables
          one-click trading: a session key that signs your orders for you, so the wallet no longer pops up on
          every trade. See <L to="learn/session-keys">One-click trading</L>.
        </p>
        <h2 id="deposit">2. Deposit</h2>
        <p>
          Click <b>Deposit</b>. From HyperCore, send USDC to the personal deposit address the app shows: it
          belongs to your wallet only, and the keeper credits it to your trading balance a few blocks later.
          From HyperEVM, approve and deposit USDC in one flow from the same window. From Arbitrum, Base,
          Ethereum and other chains, the same window burns your USDC there through Circle's CCTP and credits it
          here, no HYPE needed. See <L to="learn/deposits-withdrawals">Deposits and withdrawals</L>.
        </p>
        <h2 id="trade">3. Trade</h2>
        <p>
          Pick Long or Short, a margin within the limits the ticket shows ({MIN_MARGIN} to {MAX_MARGIN} on this deployment), and a leverage up to {MAX_LEV}, then click the
          button (or press Enter). The order fills at the worse of the book mid and the mark, within the slippage you chose. Close
          whenever you want from the Positions table: the realized PnL shows in a popup.
        </p>
        <h2 id="next">Next</h2>
        <ul>
          <li>
            <L to="learn/trades-pricing">Trades and pricing</L>: what you trade against and at which price.
          </li>
          <li>
            <L to="learn/liquidations">Liquidations</L>: where the bust price sits and what it means.
          </li>
          <li>
            <L to="learn/papurr-emissions">PAPURR emissions</L>: why losing mints PAPURR.
          </li>
        </ul>
      </>
    ),
  },
  {
    slug: 'intro/faq',
    title: 'FAQ',
    lead: 'Short answers to the questions traders ask first.',
    body: (
      <>
        <h2 id="onchain">Is it really onchain?</h2>
        <p>
          Positions, balances, the payout queue and PAPURR all live in contracts on HyperEVM, and prices come
          from HyperCore precompiles in the same block. The offchain service relays signed orders and runs
          keepers (liquidations, deposit sweeps, open interest caps), but it cannot trade for you without your
          signature or change a price. See <L to="how/architecture">Architecture</L>.
        </p>
        <h2 id="markets">Which markets, which leverage?</h2>
        <p>
          HYPE only, up to {MAX_LEV}. Margin per position goes from {MIN_MARGIN} to {MAX_MARGIN}, and a position is at most{' '}
          {MAX_NOTIONAL} of notional, whatever its leverage. Each side's open interest is capped so the house's net exposure stays within half of the
          protocol's backing: the house's effective liquidity plus the HouseReserve. See <L to="how/oi-caps">Open interest caps</L>.
        </p>
        <h2 id="cost">What does a trade cost?</h2>
        <p>
          On a win: the payout curve and a {WIN_FEE} fee on what it pays. On a loss: nothing on top of the loss. Fills
          are at the worse of the book mid and the mark, about 1 bp apart. No funding, no gas. See{' '}
          <L to="learn/wins-losses">Wins and losses</L>.
        </p>
        <h2 id="add-margin">Can I add margin to a position?</h2>
        <p>
          No. A position keeps the margin and leverage it was opened with. To change it, close it and open a
          new one, or open a second position next to it.
        </p>
        <h2 id="liquidation">What happens at liquidation?</h2>
        <p>
          When both the book mid and the mark have reached your bust price, the position is liquidated and its
          whole margin is lost. The
          bust price sits a little before the price where the margin would be exactly used up. See{' '}
          <L to="learn/liquidations">Liquidations</L>.
        </p>
        <h2 id="papurr">How do I get PAPURR?</h2>
        <p>
          By losing: every losing close mints PAPURR on what the house keeps of the loss, and a liquidation on the
          whole margin. Staked PAPURR earns HYPE bought with the protocol's revenue. See{' '}
          <L to="papurr/overview">PAPURR overview</L>.
        </p>
        <h2 id="session">Can the session key empty my account?</h2>
        <p>
          It can open and close positions and withdraw to your own HyperCore account, never to anyone else.
          Revoke it any time from the account menu. See <L to="learn/session-keys">One-click trading</L>.
        </p>
        <h2 id="frontend">Can I build my own frontend?</h2>
        <p>
          Yes. Orders are EIP-712 messages posted to the relayer API, which anyone can call. See{' '}
          <L to="dev/intents">Intents and API</L>.
        </p>
      </>
    ),
  },
];

const learn: DocPage[] = [
  {
    slug: 'learn/trades-pricing',
    title: 'Trades and pricing',
    lead: 'What a trade is, who is on the other side, and where the price comes from.',
    body: (
      <>
        <p>
          A trade is a side (long or short), a margin, and a leverage. Notional is margin × leverage: $50 at
          20× is a $1,000 position. Your PnL is the price move times the notional, settled in USDC when you
          close.
        </p>
        <h2 id="counterparty">Who you trade against</h2>
        <p>
          The house. There is no order book and no other trader on the other side: your gains are paid by the
          house and your losses go to it. That is what removes slippage and funding.
        </p>
        <h2 id="price">Where the price comes from</h2>
        <p>
          The contract reads two prices from HyperCore in the block your order executes: the mid of HYPE's order
          book and Hyperliquid's mark price. You get the worse of the two: a buy (opening a long, closing a
          short) fills at the higher, a sell at the lower. They sit about 1 bp apart, and the trade panel shows
          your exact fill price. Two checks run first:
        </p>
        <ul>
          <li>the book mid and the mark must be within 25 bp of each other, otherwise trading waits;</li>
          <li>
            the mid must stay within 25 bp of the validators' oracle price, otherwise opens, closes and
            liquidations all wait. See <L to="how/pricing">Pricing and the oracle guard</L>.
          </li>
        </ul>
        <h2 id="limits">Your price limit</h2>
        <p>
          Each order carries the worst price you accept, signed with it: your fill price at the moment you click
          (the worse of the book mid and the mark), moved by the max slippage you chose (0.1%, 0.5% or 1%). If the price moved further by the time the order
          executes, the order fails instead of filling worse. The same applies to closes.
        </p>
        <h2 id="margin">One position, one margin</h2>
        <p>
          Every position is isolated: its margin is the most it can lose, and other positions never draw on it.
          You cannot add margin, remove margin or change leverage after opening.
        </p>
        <h2 id="limits-market">Limits</h2>
        <ul>
          <li>Margin from {MIN_MARGIN} to {MAX_MARGIN} per position; the order ticket shows the live limits.</li>
          <li>Leverage from 1× to {MAX_LEV}.</li>
          <li>At most {MAX_NOTIONAL} of notional per position.</li>
          <li>
            Per-side open interest caps that bound the house's net exposure. See{' '}
            <L to="how/oi-caps">Open interest caps</L>.
          </li>
        </ul>
      </>
    ),
  },
  {
    slug: 'learn/limit-orders',
    title: 'Limit orders and take profit',
    lead: 'Orders that wait for a price: a limit order opens a position there, a take profit closes one.',
    body: (
      <>
        <p>
          A market order fills at once at the current price. A limit order and a take profit wait instead: you
          sign them now, the relayer holds them, and sends them the first block the price reaches yours. They
          fill at your price or better, never worse.
        </p>
        <h2 id="limit">Limit orders</h2>
        <p>
          Pick <b>Limit</b> in the order form, then a price, a margin and a leverage. A long waits for the
          price to fall to your limit, a short for it to rise to it. Size, bust price and the PnL preview are
          shown at your limit. A limit already at or past the current price fills at once, at the current
          (better) price.
        </p>
        <ul>
          <li>It rests 1 hour, 1 day, 7 days or 30 days, your choice, then expires.</li>
          <li>Your margin stays in your balance until the order fills: if it is not there any more by then, the order fails.</li>
          <li>Up to 20 resting orders per account. They are listed under <b>Orders</b> and drawn on the chart.</li>
        </ul>
        <h2 id="tp">Take profit</h2>
        <p>
          On an open position, <b>+ TP</b> sets a target on the winning side of the entry: above it for a long,
          below it for a short. Once the price reaches it, the position closes at the target or better. One
          take profit per position; to move it, cancel it and set a new one. If the position closes another
          way (by hand or a liquidation), its take profit goes with it.
        </p>
        <h2 id="when">When an order runs</h2>
        <p>
          The relayer compares each resting order with the fill price of every block: the price a buy pays
          (the higher of the book mid and the mark) for a long limit or a short's take profit, the price a
          sell gets (the lower) for the others. When it is at your price, the order joins the{' '}
          <L to="learn/order-queue">order queue</L>. If the price moves back before it executes, the contract
          refuses the fill and the order goes back to waiting.
        </p>
        <h2 id="trust">What the relayer can and cannot do</h2>
        <ul>
          <li>
            Your limit is part of what you sign, and the contract checks it at execution: no one can fill your
            order at a worse price.
          </li>
          <li>
            The relayer decides when to send it. It could send it late, or not at all, but only at your price or
            better.
          </li>
          <li>
            Cancelling removes the order from the relayer. The signature itself stays valid until the order
            expires, so a cancelled order could in theory still fill, at your price, until then.
          </li>
          <li>
            Signed with your one-click session key, an order cannot outlive the key: it expires with your session
            at the latest.
          </li>
        </ul>
        <Note>
          There is no stop loss: the contract can enforce a worst price, not a trigger in the losing direction.
          Your bust price is the hard stop.
        </Note>
      </>
    ),
  },
  {
    slug: 'learn/wins-losses',
    title: 'Wins and losses',
    lead: 'What a close pays, what the protocol keeps, and where the fees go.',
    body: (
      <>
        <h2 id="wins">Winning closes</h2>
        <p>Three steps turn a price move into a payout:</p>
        <ol>
          <li>
            <b>Dead zone.</b> The first 2 bp of the move do not count: a win smaller than that pays nothing. It
            removes the edge of racing price updates and of pushing Hyperliquid's book by a few bp.
          </li>
          <li>
            <b>Payout curve.</b> The rest of the gain is scaled by a ratio that grows with the size of the move:
            about 65% of the gain on a 0.1% move, 87% on 1%, close to 90% on large moves. Your position size
            does not change the ratio.
          </li>
          <li>
            <b>Near-entry cut.</b> A win closed within 0.06% of your entry (0.10% from 22:00 to 06:00 UTC and on
            weekends, when books are thinner) gives part of it back to the house: all of it at the entry, nothing
            at the edge of that band, in a straight line between. It targets bots scalping tiny moves; closes past
            the band are untouched. The positions table already shows it in your live PnL.
          </li>
          <li>
            <b>Win fee.</b> {WIN_FEE} of what the curve pays, half to the protocol and half to the PAPURR revenue
            (see <L to="learn/staking">Staking rewards</L>).
          </li>
        </ol>
        <p>
          Your margin always comes back to your balance at once. The win itself is paid at once too, unless the
          house cannot cover it: then it joins the <L to="learn/house-queue">payout queue</L>, and no fee is
          taken on it.
        </p>
        <h2 id="losses">Losing closes</h2>
        <p>
          You lose the price move times the notional, never more than your margin, and nothing on top. Of what
          you lose, 1% goes to the protocol and {STK} to the PAPURR revenue, which the buyback splits between stakers
          and the burn auction (that {STK} is skipped while the house owes more than it holds, effective liquidity
          below zero); the house keeps the rest.
        </p>
        <h2 id="why">Why wins pay the cost</h2>
        <p>
          A fee on notional would be unbearable at high leverage and a spread would hide the real price. Charging the
          outcome instead keeps every fill at the true mid. The steep part of the curve sits on the smallest
          moves, the ones a trader could try to pick off by pushing the book or racing price updates.
        </p>
        <p>
          The exact formula and its parameters are in <L to="how/payout-curve">The payout curve</L>.
        </p>
      </>
    ),
  },
  {
    slug: 'learn/liquidations',
    title: 'Liquidations',
    lead: 'Where the bust price is, and what happens when the price reaches it.',
    body: (
      <>
        <p>
          Every position shows a bust price. When both the book mid and the mark have reached it, the position
          is liquidated and its whole margin is lost: a book pushed past your bust price alone cannot liquidate
          you. The chart draws it as a dashed red line with the distance left.
        </p>
        <h2 id="buffer">The buffer</h2>
        <p>
          A position runs out of margin when the price has moved 1 / leverage against it: 1% at 100×. The bust
          price sits a little before that point, by a buffer of about 4.8 bp of price:
        </p>
        <Formula>{`long:  bust = entry × (1 − 1/leverage) × (1 + buffer)
short: bust = entry × (1 + 1/leverage) × (1 − buffer)
buffer ≈ 0.048%`}</Formula>
        <p>
          At 100×, a long opened at $80.00 busts at about $79.24 (−0.95%) instead of $79.20. At 200× it busts
          after a 0.45% move instead of 0.5%, at 500× after 0.15% instead of 0.2%. The buffer is fixed in price, so it
          weighs more as leverage grows.
        </p>
        <h2 id="what-you-lose">What you lose</h2>
        <p>
          The whole margin, even if a close just before the bust price would have returned a little. Of that
          margin, 1% goes to the protocol and {STK} to the PAPURR revenue (skipped while the house owes more than it
          holds), the house keeps the rest.
        </p>
        <h2 id="who">Who liquidates</h2>
        <p>
          A dedicated liquidator watches every open position each block and sends liquidations at the highest
          priority fee the protocol pays. Liquidation is permissionless: anyone can call it, with no reward.
          Liquidations keep running when trading is paused, but not while the oracle guard holds prices.
        </p>
      </>
    ),
  },
  {
    slug: 'learn/house-queue',
    title: 'The house and the payout queue',
    lead: 'What you trade against, how it is funded, and what happens when it cannot pay a winner at once.',
    body: (
      <>
        <h2 id="house">The house</h2>
        <p>
          The house is the protocol's pool of USDC inside the exchange contract. The protocol funds it, and it
          grows with traders' losses net of fees. It is protocol-owned: there is no LP token and nobody can
          withdraw it. Behind it sits the <L to="how/house-reserve">HouseReserve</L>, protocol capital on
          HyperCore that tops the house up when it runs short.
        </p>
        <h2 id="queue">The payout queue</h2>
        <p>
          When the house cannot pay a win in full, the win becomes a claim at the back of a first-in, first-out
          queue. As losses refill the house, the keeper pays claims from the front. Two rules keep it fair:
        </p>
        <ul>
          <li>While any claim waits, new wins join the queue too: nobody is paid ahead of an older claim.</li>
          <li>Your margin is never queued, only the profit. A queued claim is real onchain debt, not a cut.</li>
        </ul>
        <p>
          The House page shows the house liquidity, what is queued, and the open interest it faces; your queued
          claims are on the Portfolio page.
        </p>
        <h2 id="effective">Effective liquidity</h2>
        <p>
          Effective liquidity is the house balance minus queued claims and any lending deficit. It can go
          negative while claims wait.
          It sets the PAPURR mint rate, the PAPURR revenue's share of losses (none while negative), and how much
          directional exposure the open interest caps allow.
        </p>
      </>
    ),
  },
  {
    slug: 'learn/papurr-emissions',
    title: 'PAPURR emissions',
    lead: 'How losing trades mint PAPURR, and why early losses mint the most.',
    body: (
      <>
        <h2 id="how">How you earn it</h2>
        <p>
          Every losing close mints PAPURR to you, on the loss net of fees (what the house keeps); a liquidation
          mints on the whole margin. Wins mint nothing. Nothing else mints PAPURR: there is no pre-mint, no team
          or investor allocation and no airdrop. Supply starts at zero.
        </p>
        <h2 id="rate">The rate</h2>
        <p>
          During a soft launch the rate is fixed: the same PAPURR per $1 lost whatever the house holds (the Papurr
          page shows it). The timelock then switches to the curve below, with its usual delay, and the curve starts
          from zero.
        </p>
        <ul>
          {LOW_X > 0 ? (
            <li>
              While the house's effective liquidity is under {FLAT}: the curve's rate, boosted the lower the house is, up
              to ×{LOW_X} when it is empty, and never more than 100 PAPURR per $1 lost. At launch the curve is at 100, so
              this is 100 per $1 until it has decayed. A house that dips under {FLAT} later does not bring the launch
              rate back: it gets a bounded boost of the current rate.
            </li>
          ) : (
            <li>While the house's effective liquidity is under {FLAT}: a flat 100 PAPURR per $1 lost.</li>
          )}
          <li>
            Above it, the rate decays with every dollar minted on that part of the curve: it halves after about{' '}
            {usdM(HALF_M)} and is a quarter of the flat rate after {KM}.
          </li>
        </ul>
        <p>
          The decay never reverses, so early losses mint far more per dollar than later ones. The trade form
          shows how much PAPURR a liquidation would mint before you open. Details in{' '}
          <L to="how/mint-curve">The mint curve</L>.
        </p>
        <h2 id="why">Why losers</h2>
        <p>
          As on papertrade, PAPURR is the consolation for a loss: the traders whose losses fill the house get a
          share of its future revenue through staking. PAPURR costs the house nothing, and the mint rate is
          capped in the contract (at most 1,000 PAPURR per $1 lost, ten times the curve's flat rate) so no parameter
          change can flood the supply.
        </p>
      </>
    ),
  },
  {
    slug: 'learn/staking',
    title: 'Staking rewards',
    lead: 'Stake PAPURR to earn HYPE, bought with part of the PAPURR revenue, with no lockup.',
    body: (
      <>
        <h2 id="stake">Staking</h2>
        <p>
          Stake or unstake at any time from the Papurr page, with no cooldown and no lockup
          {config.minStake > 0
            ? `; a stake must leave at least ${MIN_STAKE} PAPURR staked, and unstaking everything always works`
            : ''}
          . Your stake earns from the moment it is staked, pro rata, at the current rate of the HYPE stream: arriving
          just before a purchase earns nothing of what came before it.
        </p>
        <h2 id="sources">Where rewards come from</h2>
        <p>The PAPURR revenue is made of:</p>
        <ul>
          <li>{STK} of every realized loss and liquidated margin (skipped while the house owes more than it holds).</li>
          <li>Half of the {WIN_FEE} win fee, on wins paid at once.</li>
          <li>
            Once the house's effective liquidity is above {THRESHOLD}, {NEW_HIGH} of each new record it sets above that
            level. A drop pays nothing, nor does the climb back to the previous record, and a gain is never shared
            twice.
          </li>
        </ul>
        <h2 id="split">Stakers and the burn auction</h2>
        <p>
          The PAPURR revenue is not all the stakers'. The buyback turns it into HYPE and splits every lot it books:
          {STREAM_SHARE} is streamed to stakers, {AUCTION_SHARE} goes to the pot of the burn auction, which buys
          PAPURR from its sellers and burns what nobody buys back (see{' '}
          <L to="papurr/supply-projections">Supply projections</L>). The protocol's 1% of losses and its half of the
          win fee are apart: they never enter this revenue, and the protocol takes nothing from the house's records.
        </p>
        <p>
          For example, a trader loses $100 while the house sits above {THRESHOLD}, at a new record: $1 goes to the
          protocol, {EX_STREAM} is streamed to stakers, {EX_POT} goes to the burn auction's pot, and the house keeps{' '}
          {EX_HOUSE}.
        </p>
        <h2 id="buyback">Paid in HYPE</h2>
        <p>
          At least hourly the keeper sends the PAPURR revenue, as USDC on HyperCore, to the buyback contract
          (anyone can trigger it). The buyback spends it on the HYPE/USDC book on Hyperliquid in orders of at most
          $1,000 every 5 minutes, each immediate-or-cancel and never above the HYPE oracle price plus 0.3%: an order
          that finds no seller there buys nothing and the USDC waits for the next one. Of the HYPE bought,
          {AUCTION_SHARE} goes to the burn auction's pot and {STREAM_SHARE} is streamed to stakers over 7 days; more
          HYPE spreads what is left over a new 7 days. The owner (the timelock) can
          set that period between 1 and 30 days; a change applies from the next purchase.
        </p>
        <h2 id="claim">Claiming</h2>
        <p>
          Claim from the Papurr page. By default the HYPE is sent to your HyperCore spot balance, the same address
          on Hyperliquid, where you can hold it, stake it or move it to HyperEVM; your address needs a HyperCore
          account for that, which it gets with its first deposit on Hyperliquid. You can instead have it paid to
          your wallet on HyperEVM as native HYPE: the buyback brings it back from HyperCore and the keeper sends it
          on a block or two later, less a small fee (0.0001 HYPE, kept for stakers). That is the way for a Safe or
          any contract wallet without a HyperCore account.
        </p>
        <h2 id="compound">Compounding</h2>
        <p>
          HYPE in your wallet (claimed rewards or any other) can be turned back into USDC on your trading balance
          from the Papurr page. Contributions pool into rounds that close every 30 seconds and are sold on the
          HYPE/USDC book on Hyperliquid in immediate-or-cancel orders asking at least the HYPE oracle price less
          0.3%; each order gets 15 seconds to run on Hyperliquid before the next one goes out, then the USDC
          crosses back and is deposited for each contributor pro rata, usually within a minute or two. Whatever
          three orders could not sell comes back to you as HYPE. Whole hundredths of a HYPE, at least 0.3.
        </p>
      </>
    ),
  },
  {
    slug: 'learn/deposits-withdrawals',
    title: 'Deposits and withdrawals',
    lead: 'How USDC gets into your trading balance and back out.',
    body: (
      <>
        <h2 id="deposit-core">From HyperCore</h2>
        <p>
          The app shows a deposit address that belongs to your wallet only. Send USDC to it on HyperCore (a
          spot transfer from Hyperliquid). The keeper moves it into the exchange and credits your balance,
          usually within a few blocks. Balances under $1 wait until more arrives.
        </p>
        <h2 id="deposit-evm">From HyperEVM</h2>
        <p>Approve and deposit USDC from your wallet in the Deposit window: it is credited in the same block.</p>
        <h2 id="deposit-other-chains">From another chain</h2>
        <p>
          Pick the chain in the Deposit window (Arbitrum, Base, Ethereum, OP Mainnet, Polygon, Avalanche or
          Unichain), the amount, and fast or standard. Fast transfers are attested within a minute and Circle
          charges about 1 to 1.5 bp; standard ones are free but wait for the source chain to be final, about 15 to
          20 minutes from Ethereum and the rollups that settle on it. Your wallet switches to that chain, approves
          USDC and burns it through Circle's CCTP, then switches back. Once Circle attests the burn, the keeper
          mints the USDC here and credits your trading balance in the same transaction. You pay gas on the source
          chain only, no HYPE needed. The deposit shows as <i>Bridging</i> in your transfers until it lands. How it
          works, and what you trust, is in <L to="how/deposits">Deposits</L>.
        </p>
        <h2 id="withdraw">Withdrawing</h2>
        <p>
          You can withdraw anything not locked as margin in an open position, either to your HyperCore spot
          account or to your HyperEVM wallet. A HyperCore withdrawal is signed like a trade and needs your
          address to already exist on HyperCore (any past Hyperliquid activity creates it). A HyperEVM
          withdrawal is a wallet transaction and needs a little HYPE for gas.
        </p>
        <p>
          How the deposit addresses work is in <L to="how/deposits">Deposits: per-user addresses</L>.
        </p>
      </>
    ),
  },
  {
    slug: 'learn/session-keys',
    title: 'One-click trading',
    lead: 'Why your wallet does not pop up on every trade, and what you authorize.',
    body: (
      <>
        <h2 id="how">How it works</h2>
        <p>
          Before your first trade the app creates a key in your browser and asks your wallet to sign one message
          that registers it for a limited time ({config.sessionDays} days in the app, 30 days at most in the contract). The key
          then signs your orders, and the relayer submits them onchain. The trade panel shows
          <b> 1-Click on</b> with the time left; clicking it renews the key.
        </p>
        <h2 id="can">What the key can do</h2>
        <ul>
          <li>Open and close positions on your account.</li>
          <li>Stake, unstake and claim PAPURR rewards for you.</li>
          <li>Withdraw to your own HyperCore account, and only there.</li>
        </ul>
        <h2 id="cannot">What it cannot do</h2>
        <ul>
          <li>Send funds to any other address.</li>
          <li>Register another session key.</li>
        </ul>
        <p>
          Worst case, someone who copies the key from your browser could trade your balance away, but never move
          it out. Revoke the key from the account menu at any time; it also expires on its own. A market order it
          signs is valid for 45 seconds; a limit order or a take profit until the expiry you chose, 30 days at most.
        </p>
      </>
    ),
  },
  {
    slug: 'learn/order-queue',
    title: 'The order queue',
    lead: 'What happens between your click and the fill.',
    body: (
      <>
        <p>
          Signed orders go to the relayer, which packs them into transactions to the exchange. HyperEVM's fast
          blocks hold about 3M gas each, so when many orders arrive at once some wait for the next blocks. The
          trade panel shows the congestion and your place in line.
        </p>
        <h2 id="order">Order of execution</h2>
        <ol>
          <li>Orders in the last 15 seconds of their life, so a small order is delayed but never dropped.</li>
          <li>Closes, withdrawals and staking, so nobody waits to exit behind new positions.</li>
          <li>Opens, larger notional first.</li>
          <li>Then arrival time.</li>
        </ol>
        <h2 id="cancel">Cancelling</h2>
        <p>
          While an order waits, the panel shows a Cancel button: a cancelled order leaves the queue and can never
          be resubmitted. Once a batch holds it, it can no longer be cancelled, but your price limit still
          protects you.
        </p>
      </>
    ),
  },
];

const how: DocPage[] = [
  {
    slug: 'how/architecture',
    title: 'Architecture',
    lead: 'The contracts on HyperEVM, the HyperCore features they read, and the offchain service around them.',
    body: (
      <>
        <h2 id="contracts">Contracts (HyperEVM)</h2>
        <ul>
          <li>
            <b>PapurrExchange</b>: balances, positions, settlement, the house and the payout queue. Upgradeable
            (UUPS), owned by the timelock.
          </li>
          <li>
            <b>PapurrExchangeAdmin</b>: the exchange's configuration (owner setters, guardian pause and risk
            levers), run in the exchange's own storage by delegatecall; it keeps the exchange under the contract
            size limit.
          </li>
          <li>
            <b>Executor</b>: runs batches of signed orders from allowlisted relayers; each order succeeds or fails
            on its own.
          </li>
          <li>
            <b>SessionKeyManager</b>: one-time nonces and session keys for every signed action.
          </li>
          <li>
            <b>GuardedHyperCoreOracle</b>: serves the book mid and the mark, and refuses a mid too far from the
            validators' oracle.
          </li>
          <li>
            <b>PapurrToken</b>, <b>PapurrTokenomics</b>, <b>PapurrStaking</b>: the token, its mint curve, staking and
            rewards; all three upgradeable (UUPS), owned by the timelock.
          </li>
          <li>
            <b>DepositProxyFactory</b>: one deterministic HyperCore deposit address per user.
          </li>
          <li>
            <b>CctpGateway</b>: deposits from other chains through Circle CCTP V2, minted and credited in one call.
          </li>
          <li>
            <b>HouseReserve</b>: protocol capital on HyperCore that backs the house.
          </li>
          <li>
            <b>HypeBuyback</b>: spends the PAPURR revenue on HYPE, sends {AUCTION_SHARE} of it to the burn auction and
            streams the rest to stakers, and pays their claims, on its own HyperCore account, or on HyperEVM for
            accounts without one.
          </li>
          <li>
            <b>HypeConverter</b>: turns wallet HYPE into USDC on the trading balance, through the HyperCore book.
          </li>
          <li>
            <b>PapurrLens</b>: read helpers (bust price, close preview) for frontends.
          </li>
        </ul>
        <h2 id="hypercore">HyperCore features used</h2>
        <ul>
          <li>Book best bid and ask (precompile 0x…080e) and mark price (0x…0806): the fill band.</li>
          <li>Oracle price (0x…0807): the reference the guard compares the book mid to, and the bound on the HYPE buyback's bids.</li>
          <li>
            Spot price (0x…0808): the reference and the mark of a spot market. A HIP-3 perp (a builder-deployed market
            such as xyz's) reads its book by its asset id and its oracle and mark by its perp index.
          </li>
          <li>Spot balances and account existence (0x…0801, 0x…0810): deposits and withdrawals.</li>
          <li>Borrow/lend balances (0x…0811, 0x…0812): the HouseReserve's and the house's lending.</li>
          <li>CoreWriter (0x3333…3333): spot transfers, lending actions and the buyback's HYPE buy orders, sent from contracts.</li>
        </ul>
        <h2 id="offchain">Offchain service</h2>
        <p>One codebase, several roles that only share a Postgres database and the chain:</p>
        <ul>
          <li><b>api</b>: HTTP and WebSocket for the app; verifies signatures before queueing orders.</li>
          <li><b>indexer</b>: turns contract events into the state the app shows, every block.</li>
          <li><b>relayer</b>: packs queued orders into Executor batches.</li>
          <li>
            <b>keeper</b>: deposit sweeps, open interest caps, queue payouts, reward distribution, the reserve,
            house lending, the HYPE buyback orders.
          </li>
          <li><b>liquidator</b>: liquidations only, on its own key and at the top priority fee.</li>
        </ul>
        <p>
          The service never holds user funds and cannot sign for users. Liquidations, queue payouts, sweeps,
          reward distribution, the HYPE buyback (<code>buy</code>, <code>sync</code>), booking lending yield, the
          reserve's top-ups and bringing lent house money back when it is needed are permissionless onchain, so
          anyone can run them if the service stops.
        </p>
        <h2 id="flow">A trade end to end</h2>
        <ol>
          <li>The app signs an EIP-712 open order with your session key and posts it to the API.</li>
          <li>The relayer batches it into <code>Executor.execute</code>.</li>
          <li>
            The exchange checks the signature and nonce, fills at the side of the guarded band worse for you,
            checks your price
            limit, the limits and the open interest cap, and locks your margin.
          </li>
          <li>The indexer picks up the event and the app updates over the WebSocket.</li>
        </ol>
      </>
    ),
  },
  {
    slug: 'how/pricing',
    title: 'Pricing and the oracle guard',
    lead: 'Why fills are at the worse of the book mid and the mark, and what that takes off the table.',
    body: (
      <>
        <h2 id="band">The fill band</h2>
        <p>
          On every open, close and liquidation the oracle reads two prices from HyperCore: the mid of HYPE's
          order book and Hyperliquid's mark price. They form a band, and a trade fills at the side worse for the
          trader:
        </p>
        <Formula>{`low  = min(book mid, mark)      high = max(book mid, mark)
open long / close short  (buy)  → high
open short / close long  (sell) → low
liquidation              → only once the trader's better side is past the bust price`}</Formula>
        <p>The band is usually about 1 bp wide (1.2 bp median on HYPE, measured on mainnet).</p>
        <h2 id="push">Why pushing the book buys nothing</h2>
        <p>
          A book can be pushed for a moment by someone with enough size. Against a mid-only price, they could open
          at the pushed mid and close once it springs back. The mark price is a median of three prices: the oracle
          plus its recent basis, the book, and the main centralized venues. A push moves the book, not the
          median, so the trader still fills at the mark or worse: the push pays nothing, and it cannot liquidate
          anyone either.
        </p>
        <h2 id="guard">The guard</h2>
        <p>Two circuit breakers stop trading while the prices cannot be trusted:</p>
        <ul>
          <li>
            the book mid must stay within 25 bp of the validators' oracle price (a weighted median of external spot
            prices refreshed about every 3 seconds);
          </li>
          <li>the book mid and the mark must stay within 25 bp of each other.</li>
        </ul>
        <Formula>{`|mid − oraclePrice| ≤ oraclePrice × 25 bp
|mid − mark| ≤ 25 bp of the band's center`}</Formula>
        <p>
          The guard fails closed: no oracle price, no mark or a broken book means no trade, liquidations included.
        </p>
        <h2 id="levers">Who can change it</h2>
        <p>
          The owner (timelock) sets the oracle threshold, between 0 and 5%, and the band limit per market. The
          guardian can only tighten the oracle threshold, without delay.
        </p>
      </>
    ),
  },
  {
    slug: 'how/payout-curve',
    title: 'The payout curve',
    lead: 'The exact formula for what a winning close pays.',
    body: (
      <>
        <p>For a win, with prices in HyperCore ticks:</p>
        <Formula>{`deadZone = entry / 5,000                        (2 bp)
win      = notional × (move − deadZone) / entry   (0 if move ≤ deadZone)
x        = win / notional                         (relative move after the dead zone)
g        = (1 − h) × x / (x + 1/A + h/B)
credited = win × g
paid     = credited × (1 − ${WIN_FEE})${' '.repeat(Math.max(1, 20 - WIN_FEE.length))}(win fee, half protocol, half PAPURR revenue)`}</Formula>
        <p>HYPE parameters at launch:</p>
        <table className="doc-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Value</th>
              <th>Meaning</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>h (winHaircut)</td>
              <td>10%</td>
              <td>the curve never pays more than 90% of the gain</td>
            </tr>
            <tr>
              <td>A (curveA)</td>
              <td>15,000</td>
              <td rowSpan={2}>set the move where the curve pays half its 90% maximum: 3.84 bp after the dead zone</td>
            </tr>
            <tr>
              <td>B (curveB)</td>
              <td>315</td>
            </tr>
          </tbody>
        </table>
        <table className="doc-table">
          <thead>
            <tr>
              <th>Move after the dead zone</th>
              <th>Curve pays</th>
              <th>After the {WIN_FEE} fee</th>
            </tr>
          </thead>
          <tbody>
            {[0.0005, 0.001, 0.005, 0.01, 0.05].map((move) => (
              <tr key={move}>
                <td>{`${move * 100}%`}</td>
                <td>{pct(curvePays(move))}</td>
                <td>{afterFee(move)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p>
          The ratio depends only on the move, never on the position size. HYPE's curve is calibrated on its
          volatility, about twice BTC's, so its half-payout move is twice as wide. Losses are linear: notional ×
          move / entry, capped at the margin.
        </p>
      </>
    ),
  },
  {
    slug: 'how/solvency-queue',
    title: 'Solvency and the queue',
    lead: 'The accounting that keeps every dollar owed backed, and every winner paid in order.',
    body: (
      <>
        <h2 id="buckets">Where the money sits</h2>
        <p>Every dollar the exchange holds belongs to exactly one bucket:</p>
        <ul>
          <li>traders' balances, free to trade or withdraw;</li>
          <li>margin locked in open positions;</li>
          <li>the house liquidity;</li>
          <li>dev fees and staker fees not yet distributed;</li>
          <li>staker revenue distributed but not yet sent to the HYPE buyback.</li>
        </ul>
        <Formula>{`balances + locked margin + house + dev fees + staker fees + owed to the buyback
  = USDC received − USDC sent out`}</Formula>
        <p>
          Queued claims are owed by the house on top of that: effective liquidity = house − queued claims −
          lending deficit.
        </p>
        <h2 id="win">A win</h2>
        <ul>
          <li>Queue empty and house ≥ the credited win: paid now, fee split between the protocol and the PAPURR revenue.</li>
          <li>Otherwise: the margin is returned, the net win is queued, and no fee is taken.</li>
        </ul>
        <h2 id="loss">A loss or a liquidation</h2>
        <p>
          The lost amount goes to the house, minus 1% to the protocol and {STK} to the PAPURR revenue. That part is
          read before the loss lands and is zero while the house owes more than it holds.
        </p>
        <h2 id="harvest">Paying the queue</h2>
        <p>
          <code>harvest(n)</code> pays up to n claims from the front, from the house liquidity; a claim larger
          than the liquidity is paid in part and stays first. It is permissionless, and the keeper calls it
          whenever the house can pay.
        </p>
      </>
    ),
  },
  {
    slug: 'how/mint-curve',
    title: 'The mint curve',
    lead: 'How a loss turns into PAPURR.',
    body: (
      <>
        <Formula>{`x = loss net of fees on a close, or the margin on a liquidation
L = effective house liquidity after the loss
flat part = min(x, max(0, ${FLAT} − L))${' '.repeat(Math.max(1, 20 - FLAT.length))}at ${LOW_X > 0 ? 'min(100, rate(T) × boost(L))' : '100 PAPURR per $'}
tail part = x − flat part                             at a decaying rate:
rate(T)   = 100 × (K / (K + T))²,  K = ${KM}
T         = USD already minted on the tail (only grows)${
            LOW_X > 0 ? `\nboost(L)  = 1 + ${LOW_X - 1} × min(1, (${FLAT} − L) / ${FLAT})   (×${LOW_X} at an empty house)` : ''
          }`}</Formula>
        <p>
          Integrated over a tail part of x dollars, starting at T, the tail mints 100 × x × K² / ((K + T)(K + T +
          x)) PAPURR. The rate is a quarter of the flat rate once T reaches K.
        </p>
        <p>
          A soft launch runs on <code>fixedRate</code> instead: minted = x × fixedRate, T untouched. The timelock
          sets it (<code>setFixedRate</code>, at most 1,000 per $1) and hands over to the curve with 0.
        </p>
        <table className="doc-table">
          <thead>
            <tr>
              <th>Tail volume T</th>
              <th>PAPURR per $1</th>
            </tr>
          </thead>
          <tbody>
            {[0, K / 12, HALF_M, K, 3 * K].map((tM) => (
              <tr key={tM}>
                <td>{tM === 0 ? '$0' : usdM(tM)}</td>
                <td>{rateText(tailRate(tM))}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p>
          The owner can change the parameters through the timelock (<code>setMintParams</code>,{' '}
          <code>setLowHouseMultiple</code>: ×1 to ×3); the flat rate can never exceed 1,000 PAPURR per $, and
          setting it to 0 stops emission.
        </p>
      </>
    ),
  },
  {
    slug: 'how/oi-caps',
    title: 'Open interest caps',
    lead: 'How much HYPE exposure each side may hold, and how the keeper sets it.',
    body: (
      <>
        <p>
          The exchange keeps a cap per side, in HYPE. An open that would push its side above the cap fails.
          Caps never touch open positions: closes and liquidations always work.
        </p>
        <h2 id="rule">The rule</h2>
        <Formula>{`cap(long)  = clamp(short OI + 50% × backing, ${OI_MIN}, ${OI_MAX})
cap(short) = clamp(long  OI + 50% × backing, ${OI_MIN}, ${OI_MAX})
backing    = house effective liquidity + the reserve (lent money up to what the market can return)`}</Formula>
        <ul>
          <li>
            <b>Net exposure.</b> One side can exceed the other by at most half of the protocol's backing: the
            house's effective liquidity plus the <L to="how/house-reserve">HouseReserve</L>, its spot and what it
            has lent as far as the lending market could return it now, since both reach the house within a few
            blocks when the house owes. With $1M in the house and $500K of reserve, longs can exceed shorts by
            $750K.
          </li>
          <li>
            <b>Balanced flow grows both sides.</b> When longs and shorts offset, each side can reach {OI_MAX}: the
            house's risk is the difference, not the total.
          </li>
          <li>
            <b>Floor.</b> {OI_MIN} per side, so the first trades can open while the house is small: with an empty
            house, this is the most one side can exceed the other.
          </li>
        </ul>
        <p>
          Book manipulation does not enter the caps: fills at the worse of the book mid and the mark make a pushed
          book worthless (see <L to="how/pricing">Pricing and the oracle guard</L>). The keeper updates the caps
          every 15 blocks when they move by more than 1%. Its key can only set caps: it cannot move funds or
          change prices.
        </p>
      </>
    ),
  },
  {
    slug: 'how/house-reserve',
    title: 'The HouseReserve',
    lead: 'Protocol capital on HyperCore that tops up the house, and earns lending yield while idle.',
    body: (
      <>
        <p>
          The HouseReserve is a contract holding protocol USDC on HyperCore. It is not part of the house: it
          funds the house like any donor, when needed.
        </p>
        <h2 id="backstop">Backstop</h2>
        <p>
          When the house owes more than it holds, anyone can call <code>backstop()</code>: the reserve sends the
          missing amount from its HyperCore spot towards the house, and queued wins get paid as it lands.
        </p>
        <h2 id="lending">Lending while idle</h2>
        <p>
          The reserve keeps a spot buffer and lends the rest on HyperCore's borrow/lend market. The buffer covers
          a 40% HYPE move on the house's net exposure beyond its own liquidity, and never less than $25K. The
          keeper lends the excess when the spot is 20% above the buffer, redeems when 20% below, and stops
          lending when the market is more than 90% used. Above 97% use, it redeems everything it can before
          withdrawals freeze.
        </p>
        <h2 id="powers">Who controls it</h2>
        <p>
          Only the owner (timelock) can withdraw from the reserve or inject capital. The keeper can only move
          funds between spot and lending, never out of the reserve.
        </p>
        <h2 id="house-lending">The house lends too</h2>
        <p>
          Wins and losses settle in the house's own books: no USDC moves when a position closes, only when
          someone withdraws. So part of the house liquidity can sit on the lending market as well. The owner sets
          the share allowed (at most 80% of the house) and a spot floor kept for withdrawals. The keeper keeps
          40% to 60% of the house lent, bringing it back to the middle when it leaves that band, but only from
          the spot above a withdrawal buffer and a stress move on the open exposure: it brings money back when
          the spot runs low or the market fills up, whatever the share lent, and books the yield into the house. If the house ever
          owes winners, anyone can call the money back, and anyone can bring back what is lent over the allowed
          share when the house shrinks. A loss on the lending market is booked against the house; past zero it
          is recorded as a deficit the house owes, paid back first from any later yield. Lent money counts in
          the open interest caps only as far as the market could return it now.
        </p>
      </>
    ),
  },
  {
    slug: 'how/deposits',
    title: 'Deposits: per-user addresses and other chains',
    lead: 'How a plain HyperCore transfer, or a burn on another chain, becomes a trading balance.',
    body: (
      <>
        <p>
          Every wallet has a deposit address computed with CREATE2 from the factory, the wallet address and a
          fixed proxy code. Anyone can compute it before it exists, so it can receive USDC on HyperCore first.
        </p>
        <h2 id="sweep">The sweep</h2>
        <ol>
          <li>
            <code>requestSweep(user)</code> deploys the proxy if needed and has it send its whole HyperCore spot
            USDC to the exchange through CoreWriter.
          </li>
          <li>
            <code>creditSweep(user)</code>, one block later, credits that amount to the user's balance.
          </li>
        </ol>
        <p>
          Both calls are permissionless, but they can only move a user's deposit into that user's balance. The
          keeper watches addresses that asked for a deposit every block for 30 minutes, then everyone every few
          seconds.
        </p>
        <h2 id="cctp">From another chain</h2>
        <p>
          A deposit from Arbitrum, Base, Ethereum or another chain Circle serves is a CCTP V2 burn. The app calls
          Circle's <code>TokenMessengerV2.depositForBurnWithHook</code> there with the gateway as mint recipient
          and destination caller, and your address as hook data. Circle's attestation service signs the burn once
          the source block is confirmed (fast) or final (standard); the keeper then calls{' '}
          <code>CctpGateway.finalize(message, attestation)</code>, which has Circle's MessageTransmitterV2 mint the
          USDC to the gateway and deposits it for you (<code>PapurrExchange.depositFor</code>) in the same
          transaction. The credit is what was minted: the burned amount less Circle's fee.
        </p>
        <p>
          Anyone holding the message and attestation (Circle's API hands them out) can call <code>finalize</code>,
          so a stopped keeper only delays the credit. Naming the gateway as destination caller means nobody else
          can have the USDC minted there without crediting you. Circle's attestation is valid 24 hours; the keeper
          asks for a new one if it expires.
        </p>
        <h2 id="trust">What you trust</h2>
        <p>
          The factory is not upgradeable, so your deposit address always maps to the same code. A proxy can only
          send its full balance to the exchange, on the exchange's call.
        </p>
        <p>
          The gateway is not upgradeable either, holds nothing between transactions and can only credit the account
          the burn names (its hook data, or the burn's sender). Its owner, the timelock, can only send on tokens
          that reached it by mistake. The burn itself relies on Circle's attestation, as every CCTP transfer does.
        </p>
      </>
    ),
  },
  {
    slug: 'how/risk',
    title: 'Risks and admin keys',
    lead: 'What can go wrong, what limits it, and who can change what.',
    body: (
      <>
        <h2 id="risks">Main risks</h2>
        <ul>
          <li>
            <b>Price manipulation.</b> Filling at the worse of the book mid and the mark makes a pushed book
            worthless: the mark is a median that also follows the oracle and centralized venues. What remains is
            the gap between the mark's own inputs, about a bp, inside the 2 bp dead zone.
          </li>
          <li>
            <b>House drawdown.</b> A strong one-way move can leave the house owing more than it holds. Wins then
            queue until losses or the HouseReserve refill it.
          </li>
          <li>
            <b>Smart contract risk.</b> The contracts are upgradeable by the owner, behind a timelock.
          </li>
          <li>
            <b>Offchain service.</b> If the relayer stops, new orders stop; your funds stay onchain, and
            liquidations, sweeps, queue payouts, reward distribution and the HYPE buyback can be called by anyone.
          </li>
        </ul>
        <h2 id="roles">Roles</h2>
        <table className="doc-table">
          <thead>
            <tr>
              <th>Role</th>
              <th>Can</th>
              <th>Cannot</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Owner (timelock, run by a multisig)</td>
              <td>
                list markets and change their parameters, fees (each at most 10%; dev plus PAPURR revenue share of a
                loss at most 9%), reward shares, margin limits, the mint curve, the oracle guard threshold, house
                lending's cap and floor, the buyback's pace and price bound, what it keeps from a claim paid on
                HyperEVM (at most 0.01 HYPE), the stakers' stream duration (1 to 30 days), where idle stakers'
                unclaimed HYPE is swept, the burn auction's share of each lot (at most half), its epoch length (1
                to 30 days), its buy-back premium and fee (at most 50% and 10%) and where that fee goes, the
                converter's price bound (at most 2%), minimum, pace and attempts; swap keepers and relayers;
                upgrade contracts
              </td>
              <td>act without the timelock delay</td>
            </tr>
            <tr>
              <td>Guardian (multisig)</td>
              <td>
                pause opens, closes or one market; lower max leverage and notional per position; tighten the
                oracle guard; stop house lending; halt the HYPE buyback; halt HYPE conversions
              </td>
              <td>raise any limit, move funds, pause liquidations</td>
            </tr>
            <tr>
              <td>Keeper</td>
              <td>
                set open interest caps; move the reserve between spot and lending; lend and redeem house liquidity
                within the owner's cap and floor
              </td>
              <td>move user funds, change prices</td>
            </tr>
            <tr>
              <td>Relayer</td>
              <td>submit signed orders, choose their order within their deadline</td>
              <td>forge an order or fill past its price limit</td>
            </tr>
          </tbody>
        </table>
        <Note>
          Pausing closes never pauses liquidations, so a pause cannot leave a losing position open past its bust
          price.
        </Note>
      </>
    ),
  },
];

const papurr: DocPage[] = [
  {
    slug: 'papurr/overview',
    title: 'PAPURR overview',
    lead: 'The protocol token: minted to traders on their losses, staked for a share of revenue.',
    body: (
      <>
        <ul>
          <li>
            ERC-20 with permit, 18 decimals, symbol PAPURR. Only the tokenomics contract can mint it; anyone can
            burn their own (<code>burn</code>, <code>burnFrom</code>), and the contract counts what was ever
            burned (<code>totalBurned</code>). The token is upgradeable (UUPS) by the timelock, like the exchange:
            its rules can follow the protocol's, each change behind the timelock's delay. The burn auction pays HYPE for
            PAPURR sold to it, resells it dearer for one epoch and burns what nobody takes: see{' '}
            <L to="papurr/staking">Staking and claims</L>.
          </li>
          <li>
            Not transferable between wallets: PAPURR only moves into and out of the protocol's contracts (staking,
            the burn auction) and integrations the timelock whitelists. A token that trades freely can be shorted,
            and a short on it would pay whoever drains the house on top of the drain itself; kept off markets, an
            attack on the house can only earn the house. The timelock can open transfers for good later
            (<code>openTransfers</code>), never close them again.
          </li>
          <li>Supply starts at zero and grows only from losing trades and liquidations.</li>
          <li>No pre-mint, no team or investor allocation, no airdrop, no vesting.</li>
        </ul>
        <h2 id="supply">How supply grows</h2>
        <p>
          100 PAPURR per $1 lost while the house is under {FLAT} of effective liquidity, then a decaying
          rate. See <L to="learn/papurr-emissions">PAPURR emissions</L> and <L to="how/mint-curve">The mint
          curve</L>. What the burn auction takes back out over the years: <L to="papurr/supply-projections">Supply
          projections</L>.
        </p>
        <h2 id="value">What it earns</h2>
        <p>
          Staked PAPURR earns HYPE: {STREAM_SHARE} of the PAPURR revenue ({STK} of losses, half of the win fees, and a
          share of the house's gains above {THRESHOLD}), the other {AUCTION_SHARE} paying the burn auction. See <L to="learn/staking">Staking rewards</L>.
        </p>
      </>
    ),
  },
  {
    slug: 'papurr/staking',
    title: 'Staking and claims',
    lead: 'The mechanics behind stake, unstake and claim.',
    body: (
      <>
        <p>
          PapurrStaking streams HYPE: each purchase sets a rate, the HYPE bought plus what was still to come,
          spread over <code>rewardDuration</code> (7 days, settable by the timelock between 1 and 30 days). A
          reward-per-share accumulator grows with that rate divided by the PAPURR staked;
          your pending reward is your stake times the growth since your last action. While nothing is staked the
          stream is kept and paid out again with the next purchase.
        </p>
        <ul>
          <li>
            <b>Stake / unstake</b>: instant, signed by your wallet or session key, submitted by the relayer.
          </li>
          <li>
            <b>Distribute</b>: <code>PapurrExchange.distribute()</code> sends the PAPURR revenue (its fees and its share of
            the house's gains) to <code>HypeBuyback</code> as HyperCore spot USDC. Permissionless, called by the keeper
            at least hourly; what the spot cannot cover (it keeps a floor for withdrawals) waits for the next call.
          </li>
          <li>
            <b>Buy</b>: <code>HypeBuyback.buy()</code> books the HYPE received so far, then sends one
            immediate-or-cancel order for at most $1,000 and at least $15, priced at the HYPE oracle price plus 0.3%
            (rounded to a valid tick), at most every 5 minutes; <code>sync()</code> books HYPE that arrived without
            buying. Permissionless: size, price bound and pace are fixed in the contract.
          </li>
          <li>
            <b>Claim</b>: the buyback sends your HYPE to your HyperCore spot account (<code>claim</code>), or,
            with <code>claimToEvm</code>, queues it for your HyperEVM address: the HYPE is spot-sent to the system
            address 0x2222…2222, which credits the buyback itself on HyperEVM the next block, and anyone (the
            keeper does) calls <code>HypeBuyback.settleEvm</code>, which pays the queue in order as native HYPE.
            A recipient that refuses the transfer keeps it claimable with <code>withdrawEvm</code>. The buyback
            keeps <code>evmFeeWei</code> (0.0001 HYPE at deployment, at most 0.01) from such a claim, on its
            HyperCore account, where the next <code>sync</code> books it for stakers.
          </li>
          <li>
            <b>Convert</b>: <code>HypeConverter.convert</code> takes native HYPE (whole hundredths, at least 0.3)
            into the open round and bridges it to the converter's HyperCore account. One round moves at a time, one
            step per block (<code>step</code>, permissionless, the keeper calls it every ten blocks): the open round closes, an
            immediate-or-cancel sell of its HYPE goes out at the oracle price less 0.3% at most, up to three times
            for what is left, then the USDC (and any HYPE unsold) are spot-sent to the system addresses and cross
            back to the converter on HyperEVM, where <code>credit</code> deposits each contributor's share on the
            exchange (<code>depositFor</code>) and returns the unsold HYPE pro rata. Like the buyback, the
            converter only ever sends orders and spot sends from its account.
          </li>
          <li>
            <b>Burn auction</b>: {AUCTION_SHARE} of every HYPE lot the buyback books goes to{' '}
            <code>PapurrBurnAuction</code> instead of the stream. Each epoch (3 days on mainnet) spends that pot on PAPURR at one clearing price. You offer
            PAPURR with the lowest price you accept (a tick of a fixed grid, a quarter apart, or "any price");
            when the epoch closes, offers are taken from the cheapest up as long as the pot pays their price for
            everything taken so far, and every taken offer is paid the same price: the pot over what was taken,
            never below anyone's minimum. Offers above that are left out and the PAPURR comes back to its seller
            at the next claim. An offer cannot be withdrawn while the epoch runs, but its minimum can be moved; a
            late large offer at a lower price can only push yours out, not cut what it pays. An epoch that takes
            nothing keeps its pot for the next; claims go to your HyperCore account once the epoch ends. The
            PAPURR taken is not burned at once: while the next epoch runs, anyone can buy it back
            (<code>buy</code>, paid in HYPE from the wallet) at the price the epoch paid plus 10%, all of which is
            streamed to stakers like a bought lot, plus 1% for the protocol's dev wallet; what is left when that
            epoch closes is burned. The share (at most half), the epoch length (1 to 30 days), the premium and fee
            (at most 50% and 10%) and the fee's recipient are the timelock's. What this can take out of the supply
            over the years: <L to="papurr/supply-projections">Supply projections</L>.
          </li>
          <li>
            <b>Idle accounts</b>: after 100 days without a stake, unstake or claim from your address, anyone can
            call <code>sweep</code> on it, which sends its unclaimed HYPE to the protocol's dev wallet. Your staked
            PAPURR is never touched and keeps earning; any stake, unstake or claim (even of nothing) restarts the
            100 days. The Papurr page shows how many days are left.
          </li>
        </ul>
        <p>
          The buyback only ever sends two HyperCore actions, buy orders and HYPE transfers (to stakers, to auction
          sellers, or back to itself on HyperEVM for claims paid there): it can never authorize a key to act for its
          account, so nobody can, for example, register a referrer on it. The same goes for the converter: sell
          orders and transfers to the system addresses, nothing else.
        </p>
      </>
    ),
  },
  {
    slug: 'papurr/supply-projections',
    title: 'Supply projections',
    lead: 'What the burn auction takes out of the supply over time, and what that depends on.',
    body: (
      <>
        <p>
          The supply moves two ways: losses mint PAPURR, and the burn auction destroys what it buys and nobody
          buys back. The first is set by the mint rate, the second by a HYPE budget and by the price sellers
          accept. This page follows the money, then projects the supply over ten years under assumptions you can
          change.
        </p>
        <h2 id="budget">The auction's budget</h2>
        <p>
          Of every $1 a trader loses, {STK} goes to the PAPURR revenue (plus half of the {WIN_FEE} win fee on wins and{' '}
          {NEW_HIGH} of the house's new records above {THRESHOLD}, left out of the figures below). The buyback turns
          it into HYPE: {AUCTION_SHARE} of every lot it books is the auction's pot, {BUDGET_CENTS} per $1 lost, and the
          other {STREAM_SHARE} is streamed to stakers.
          The pot is spent on PAPURR at the price the epoch clears at, and what nobody buys back during the next
          epoch is burned: PAPURR burned = pot / clearing price. A pot nothing is sold into carries over to the
          next epoch, so no HYPE is lost.
        </p>
        <table className="doc-table">
          <thead>
            <tr>
              <th>Net losses per month</th>
              <th>Pot per month</th>
              <th>Pot per year</th>
            </tr>
          </thead>
          <tbody>
            {[250_000, 1_000_000, 5_000_000].map((m) => (
              <tr key={m}>
                <td>{fmtMoney(m)}</td>
                <td>{fmtMoney(m * BUDGET)}</td>
                <td>{fmtMoney(m * BUDGET * 12)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <h2 id="break-even">When the supply shrinks</h2>
        <p>
          Over any period, the supply falls when the auction burns more than losses mint: pot / price above
          rate × losses, that is a clearing price under {BUDGET_CENTS} divided by the mint rate. Above that price
          the auction slows the growth of the supply; it does not reverse it.
        </p>
        <table className="doc-table">
          <thead>
            <tr>
              <th>Mint rate (PAPURR per $1)</th>
              <th>Where</th>
              <th>Supply shrinks under</th>
            </tr>
          </thead>
          <tbody>
            <SoftLaunchBreakEven />
            <tr>
              <td>100</td>
              <td>the start of the curve</td>
              <td>{breakEven(100)}</td>
            </tr>
            {[25, 4, 1].map((rate) => (
              <tr key={rate}>
                <td>{rate}</td>
                <td>the curve after {tailAt(rate)} of tail losses</td>
                <td>{breakEven(rate)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p>
          The curve does the rest: its tail mints at most 100 × K, {CURVE_TOTAL} PAPURR in all, however much is
          lost, while the auction's budget has no end. Sooner or later the burn outweighs the mint at any price;
          how soon depends on the flow of losses and on the price below.
        </p>
        <h2 id="price">The price sellers accept</h2>
        <p>
          The protocol sets no price: the pot is the bid, sellers name the least they take, and the lowest asks
          are served first. What a holder gives up by selling is the staking stream, {STREAM_SHARE} of the
          buyback's HYPE (plus what buy-backs pay). A holder who wants a yield of y a year on the value of their
          stake values 1 PAPURR at {STREAM_SHARE} of the yearly revenue over y times the staked supply. At that
          price the pot buys {config.auctionPct}/{100 - config.auctionPct} × y of the staked supply every year,
          whatever the revenue: more revenue lifts the price as much as the pot. The buy-back closes the other
          side: PAPURR cleared far under what holders think it is worth gets bought back at the clearing price plus
          10% (paying stakers) rather than burned, so over time the burn settles near that value.
        </p>
        <table className="doc-table">
          <thead>
            <tr>
              <th>Yield holders demand</th>
              <th>Burned per year, share of the staked supply</th>
            </tr>
          </thead>
          <tbody>
            {[10, 25, 50, 100].map((y) => (
              <tr key={y}>
                <td>{y}%</td>
                <td>{burnShare(y)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <h2 id="projection">Ten years, under your assumptions</h2>
        <p>
          A steady flow of net losses, holders selling at the value of the yield they demand, a share of the
          supply staked, and the mint rate: the curve from zero with the house above {FLAT} so that losses
          advance its tail, or the soft launch's fixed rate. The implied price is what that yield makes one
          PAPURR worth at the end of each year.
        </p>
        <BurnProjection />
        <Note>
          A model, not a forecast. Losses come in waves, the yield holders demand moves with the market,
          buy-backs return some PAPURR to circulation, and the timelock can change the auction's share (at most
          half of each lot), the epoch, the premium and the mint parameters, each with its delay. Win fees and the
          house's records add to the PAPURR revenue, so to the stakers' stream and to the pot; the model leaves them out.
        </Note>
      </>
    ),
  },
];

const dev: DocPage[] = [
  {
    slug: 'dev/intents',
    title: 'Intents and API',
    lead: 'How to trade from your own code or frontend.',
    body: (
      <>
        <p>
          Every action is an EIP-712 message signed by the user's wallet or a live session key, posted to the
          relayer API, which batches it onchain.
        </p>
        <h2 id="types">Signed types</h2>
        <Formula>{`PapurrExchange domain ("PapurrExchange", "1", chainId 999)
OpenPosition(address user,uint32 instrumentId,bool isLong,uint128 margin,uint32 leverage,
             uint64 acceptablePx,uint256 nonce,uint64 deadline)
Close(address user,uint256[] positionIds,uint64[] limitPx,uint256 nonce,uint64 deadline)
WithdrawToCore(address user,uint256 amount,uint256 nonce,uint64 deadline)`}</Formula>
        <ul>
          <li>Amounts are USD with 18 decimals; prices are HyperCore ticks (HYPE: 4 decimals).</li>
          <li>
            <code>acceptablePx</code> / <code>limitPx</code>: worst fill, 0 for any price.
          </li>
          <li>Nonces are random and single-use (a bitmap per user); deadlines are unix seconds.</li>
        </ul>
        <h2 id="api">API</h2>
        <ul>
          <li>
            <code>POST /intents</code> with <code>{'{ action, request, signature }'}</code>, bigints as decimal
            strings. Returns the intent id.
          </li>
          <li>
            Add <code>{'"type": "limit"'}</code> to rest an order until the price reaches it: an open with its{' '}
            <code>acceptablePx</code> as the limit, or a close of one position with its <code>limitPx</code> as the
            take profit. It waits as <code>waiting</code>, up to 30 days and never past the signing session key.
          </li>
          <li>
            <code>GET /intents/:id</code>: waiting, pending, submitted, succeeded, failed (with the revert
            reason), expired or cancelled.
          </li>
          <li>
            <code>POST /intents/:id/cancel</code>: signed cancel while still waiting or pending.
          </li>
          <li>
            <code>GET /snapshot</code>, <code>GET /stats</code>, <code>GET /users/:address/account</code>.
          </li>
          <li>
            <code>GET /stats/series?range=30d</code> (7d, 30d, 90d, all): the Stats page's history, one row
            per hour over 7 days and per day beyond (volume, trades, traders, PnL, fees, deposits, mints,
            distributions, burns), plus hourly samples of the protocol state. <code>GET /stats/epochs</code>:
            the burn auction's closed epochs.
          </li>
          <li>
            <code>GET /ws</code>: WebSocket. The server pushes the market every block; subscribe with{' '}
            <code>{'{"op":"subscribe","user":"0x…"}'}</code> for your wallet, positions and orders.
          </li>
        </ul>
      </>
    ),
  },
  {
    slug: 'dev/contracts',
    title: 'Contract addresses',
    lead: 'Papurrtrade on HyperEVM mainnet (chain 999).',
    body: (
      <>
        <Note>
          Mainnet addresses are published here at launch, each checked onchain. Never send funds to an address that
          is not listed on this page.
        </Note>
        <table className="doc-table">
          <thead>
            <tr>
              <th>Contract</th>
              <th>Role</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>PapurrExchange</td>
              <td>balances, positions, house, queue</td>
            </tr>
            <tr>
              <td>PapurrExchangeAdmin</td>
              <td>configuration module of the exchange</td>
            </tr>
            <tr>
              <td>Executor</td>
              <td>batches of signed orders</td>
            </tr>
            <tr>
              <td>SessionKeyManager</td>
              <td>nonces and session keys</td>
            </tr>
            <tr>
              <td>GuardedHyperCoreOracle</td>
              <td>guarded book mid and mark (fill band)</td>
            </tr>
            <tr>
              <td>PapurrToken / PapurrTokenomics / PapurrStaking</td>
              <td>token, mint curve, staking</td>
            </tr>
            <tr>
              <td>DepositProxyFactory</td>
              <td>per-user deposit addresses</td>
            </tr>
            <tr>
              <td>CctpGateway</td>
              <td>deposits from other chains (Circle CCTP V2)</td>
            </tr>
            <tr>
              <td>HouseReserve</td>
              <td>protocol capital on HyperCore</td>
            </tr>
            <tr>
              <td>HypeBuyback</td>
              <td>PAPURR revenue spent on HYPE, for the stakers' stream and the burn auction</td>
            </tr>
            <tr>
              <td>HypeConverter</td>
              <td>wallet HYPE into trading USDC</td>
            </tr>
            <tr>
              <td>PapurrBurnAuction</td>
              <td>HYPE pots paid to PAPURR sellers by epoch; buy-backs at a mark-up, then burns</td>
            </tr>
            <tr>
              <td>Collateral: USDC</td>
              <td>
                Circle's ERC-20 <code>0xb88339CB7199b77E23DB6E890353E22632Ba630f</code>; the <code>0x6b9e…0a24</code>
                that Hyperliquid's spot metadata lists is Circle's HyperEVM–HyperCore transfer contract, not the token
              </td>
            </tr>
          </tbody>
        </table>
      </>
    ),
  },
];

export const DOCS: DocSection[] = [
  { label: 'Introduction', pages: intro },
  { label: 'How it works', pages: learn },
  { label: 'Under the hood', pages: how },
  { label: '$PAPURR', pages: papurr },
  { label: 'Developers', pages: dev },
];

export const DOC_PAGES: DocPage[] = DOCS.flatMap((s) => s.pages);
